import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Global } from '../models/global.model';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MarkdownModule, MarkdownService } from 'ngx-markdown';
import { MatButtonModule } from '@angular/material/button';
import { SessionStorageService } from '../services/session-storage.service';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { WarnDialogComponent } from '../warn-dialog/warn-dialog.component';
import { PostTradeService } from '../post-trade/post-trade.service';
import { throwError } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AiSummaryResponse } from '../models/post-trade-models';

@Component({
  selector: 'app-ai-summary',
  imports: [
    NgxEditorModule,
    FormsModule,
    MatCheckboxModule,
    MarkdownModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatIconModule,
    CommonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './ai-summary.component.html',
  styleUrl: './ai-summary.component.scss',
})
export class AiSummaryComponent implements OnInit {
  private markdownService = inject(MarkdownService);
  private dialog = inject(MatDialog);
  private sessionStorage = inject(SessionStorageService);
  private postTradeService = inject(PostTradeService);
  private changeDetector = inject(ChangeDetectorRef);

  @Output() summaryCheck = new EventEmitter();
  @Output() summaryTextChange = new EventEmitter();
  @Output() saveSummary = new EventEmitter();
  @Output() revertOutput = new EventEmitter();
  @Input() title = false;
  @Input() text = '';
  @Input() edit = false;
  @Input() aiSummaryReadOnly: boolean;
  @Input() summaryLoading: boolean;
  @Input() aiSummaryData: {
    AICaseSummaryCheckBoxText: string;
    AICaseSummaryDisclosure: string;
    AICaseSummaryLabel: string;
    AICaseSummaryPlaceholder: string;
    AICaseSummaryPrompt: string;
    AICaseSummaryPromptName: string;
    AICaseSummaryGroup: string;
    AICaseSummaryValidated: boolean;
    AINoteConfigurationID: string;
    AWDocumentReadingAIPromptID: string;
    IWDocumentReadingAIPromptID: string;
    IncludeDocumentReadingAW: boolean;
    IncludeDocumentReadingIW: boolean;
    IncludeDocumentReadingPP: boolean;
    MaxSubmissions: number;
    PPDocumentReadingAIPromptID: string;
    ShowAICaseSummary: boolean;
    SummaryValidated: boolean;
    LastInteraction: any;
    profileId: string;
  } = null;
  aiBtnExpand = false;
  aiSummaryBtnText = 'Generate';
  globals: Global;
  loading = false;
  profile;
  rights: string[];
  editor: Editor;
  submittedEditor: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    ['undo', 'redo'],
  ];
  message = '';
  caseSummaryLabel: string;
  caseSummaryPrompt: string;
  caseSummaryPlaceholder: string;
  caseSummaryChecked = false;
  caseSummaryCheckBoxText: string;
  caseSummaryDisclosure: string;
  currentApprovedText: string = '';
  messageControl = new FormControl();
  noText = false;
  ogText: any;
  manualEdit: boolean;
  neverChecked = true;
  initText: boolean;
  mostRecentSummarySearchResult: {
    data: {
      CreatedDate: string;
      ReferenceID: string;
      Text: string;
      Type: string;
      id: number;
    }[];
  };
  aiGenerateBtn: boolean;
  default;
  defaultSearchPayload: {
    search: {
      CreatedDate: { start: string; end: string };
      ReferenceID: { searchString: string };
    };
    skip: string;
    take: string;
    sort: { CreatedDate: string };
  };
  generateSummaryLoading: boolean;

  ngOnInit(): void {
    this.editor = new Editor();
    this.messageControl.disable();

    if (this.aiSummaryData) {
      this.setUpDisplay();
    }

    if (this.aiSummaryReadOnly) {
      this.messageControl.disable();
    }
  }

  editorChange(value): void {
    if (value === '<p></p>') {
      this.caseSummaryPlaceholder = 'Case Summary';
    }

    this.textChanged();
  }

  triggerGenerateSummary() {
    this.generateSummaryLoading = true;
    this.text = '';
    this.messageControl.setValue('');
    this.caseSummaryPlaceholder = this.aiSummaryData.AICaseSummaryPlaceholder;
    this.aiSummaryReadOnly = true;
    this.generateSummary().subscribe({
      next: () => {
        this.generateSummaryLoading = false;
        this.aiSummaryReadOnly = false;
      },
      error: err => {
        console.error('Error: Could not generate summary.', err);
      },
    });
  }

  generateSummary() {
    return this.postTradeService
      .aiBulkProcess({
        type: 'aicasereview',
        profileids: [this.aiSummaryData.profileId],
        promptname: this.aiSummaryData?.AICaseSummaryPromptName,
        group: this.aiSummaryData?.AICaseSummaryGroup,
      })
      .pipe(
        map((newRes: AiSummaryResponse) => {
          this.aiGenerateBtn = false;
          if (newRes?.result) {
            (async () => {
              this.text = await this.markdownService.parse(newRes.result);
              this.changeDetector.detectChanges();
            })();
          }
          return { data: [{ Text: newRes.result, Type: 'aicasereview' }] };
        })
      );
  }

  triggerControlObs() {
    this.messageControl.valueChanges.pipe().subscribe(value => {
      this.editorChange(value);
    });
  }

  setUpDisplay() {
    this.caseSummaryLabel = this.aiSummaryData.AICaseSummaryLabel;
    this.caseSummaryPrompt = this.aiSummaryData.AICaseSummaryPrompt;
    this.caseSummaryPlaceholder = this.aiSummaryData.AICaseSummaryPlaceholder;
    this.caseSummaryCheckBoxText = this.aiSummaryData.AICaseSummaryCheckBoxText;
    this.caseSummaryDisclosure = this.aiSummaryData.AICaseSummaryDisclosure;
    this.caseSummaryChecked = this.aiSummaryData.SummaryValidated;

    this.defaultSearchPayload = {
      search: {
        CreatedDate: {
          start: new Date(0).toISOString(),
          end: new Date().toISOString(),
        },
        ReferenceID: { searchString: this.aiSummaryData.profileId },
      },
      skip: '0',
      take: '1',
      sort: { CreatedDate: 'desc' },
    };

    this.getAiSummaries().subscribe({
      next: () => {
        this.messageControl.enable();
        this.summaryLoading = false;
        this.triggerControlObs();
        this.changeDetector.detectChanges();
      },
      error: err => {
        this.summaryLoading = false;
        console.error('Error:', err);
      },
    });
  }

  getAiSummaries(paramSearchPayload?) {
    let recentSearchPayload = this.defaultSearchPayload;
    if (paramSearchPayload) {
      recentSearchPayload = paramSearchPayload;
    }

    return this.postTradeService.getAiSummarySearch(recentSearchPayload).pipe(
      switchMap(
        (res: {
          data: {
            CreatedDate: string;
            ReferenceID: string;
            Text: string;
            Type: string;
            id: number;
          }[];
        }) => {
          const hasValidData = !!(
            res?.data &&
            res.data.length > 0 &&
            res.data[0].Text
          );

          if (hasValidData) {
            const summaryEdited = res.data[0].Type === 'aicasereview_edited';
            this.aiGenerateBtn =
              summaryEdited &&
              new Date(res.data[0].CreatedDate) <
                new Date(this.aiSummaryData.LastInteraction);
          }
          if (
            (hasValidData &&
              new Date(res.data[0].CreatedDate) <
                new Date(this.aiSummaryData.LastInteraction) &&
              !this.aiGenerateBtn) ||
            !hasValidData
          ) {
            return this.generateSummary();
          }

          if (hasValidData || paramSearchPayload) {
            (async () => {
              this.text = await this.markdownService.parse(
                res.data[0].Text || ''
              );
              this.messageControl.setValue(
                await this.markdownService.parse(res.data[0].Text || '')
              );
              if (this.aiSummaryData.SummaryValidated) {
                this.neverChecked = false;
                this.currentApprovedText = this.messageControl.value;
                this.caseSummaryChecked = this.aiSummaryData.SummaryValidated;
              }
              this.mostRecentSummarySearchResult = res;
              this.changeDetector.detectChanges();
            })();

            return [res];
          }
        }
      ),
      catchError(error => {
        console.error('Error getting summaries:', error);
        return throwError(() => new Error('Error getting summaries.'));
      })
    );
  }

  revertOriginal(): void {
    const payload = {
      search: {
        CreatedDate: {
          start: new Date(0).toISOString(),
          end: new Date().toISOString(),
        },
        ReferenceID: { searchString: this.aiSummaryData.profileId },
      },
      skip: '0',
      take: '9999',
      sort: { CreatedDate: 'desc' },
    };
    this.getAiSummaries(payload).subscribe({
      next: res => {
        (async () => {
          this.messageControl.setValue(
            await this.markdownService.parse(
              res.data.find(dataItem => dataItem.Type === 'aicasereview').Text
            )
          );
          this.text = await this.markdownService.parse(
            res.data.find(dataItem => dataItem.Type === 'aicasereview').Text
          );
          this.changeDetector.detectChanges();
        })();

        this.changeDetector.detectChanges();
      },
      error: err => {
        console.error('Error:', err);
      },
    });
  }

  summaryCheckChanged(ev) {
    this.caseSummaryChecked = typeof ev === 'boolean' ? ev : ev.checked;
    this.neverChecked = false;
    if (ev) {
      this.currentApprovedText = this.messageControl.value;
    }
    if (Object.prototype.hasOwnProperty.call(ev, 'checked')) {
      this.summaryCheck.emit(ev.checked);
    } else {
      this.summaryCheck.emit(ev);
    }
  }

  submitSummary() {
    this.saveSummary.emit(this.messageControl.value);
  }

  decodeHtml(html: string): string {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    const decodedText = tempElement.textContent || tempElement.innerText || '';
    if (
      !decodedText.replace(/[\r\n]+/g, '') ||
      decodedText.replace(/[\r\n]+/g, '') === 'undefined'
    ) {
      return '';
    }
    return decodedText.replace(/[\r\n]+/g, ''); // Remove all newline characters
  }

  textChanged() {
    if (
      this.decodeHtml(this.messageControl.value) &&
      this.decodeHtml(this.currentApprovedText)
    ) {
      if (
        this.decodeHtml(this.messageControl.value) !==
        this.decodeHtml(this.currentApprovedText)
      ) {
        if (this.caseSummaryChecked) {
          this.caseSummaryChecked = false;
          this.summaryCheckChanged(false);
        }
      } else {
        if (!this.caseSummaryChecked) {
          this.caseSummaryChecked = true;
          this.summaryCheckChanged(true);
        }
      }
      this.summaryTextChange.emit(this.messageControl.value);
      this.changeDetector.detectChanges();
    }
  }

  revertOriginalTrigger(ev): void {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Are You Sure?',
        content: `This will replace the current text with the most recent generated text.`,
        confirm: 'Revert',
        cancel: 'Cancel',
        hideCancel: false,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.revertOriginal();
      }
      dialogRef = null;
    });
  }
}
